"use client";

import React, { useRef, useState, useCallback, useEffect } from "react";
import { Carousel } from "@components/molecules/Carousel";
import { AllFeatureCard } from "@components/molecules/AllFeatureCard";
import Text from "@components/atoms/Text";
import styles from "./AllFeaturesCarousel.module.scss";

interface TransformedFeature {
  id: string;
  title: string;
  description: string;
  image: string;
  availableVariants: string;
  available: any;
  category: string;
}

interface AllFeaturesCarouselClientProps {
  allFeatures: TransformedFeature[];
  isSellable: boolean;
  isMMV?: boolean;
  isInApp?: boolean;
  className?: string;
  mmvName?: string;
}


export const AllFeaturesCarouselClient: React.FC<AllFeaturesCarouselClientProps> = React.memo(({
  allFeatures,
  isSellable,
  isMMV,
  className = "",
  mmvName,
  isInApp
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const getSlideLeftPosition = useCallback((index: number) => {
    const container = scrollRef.current;
    if (!container) return null;

    const slides = Array.from(container.children) as HTMLElement[];
    const targetSlide = slides[index];
    const firstSlide = slides[0];

    if (!targetSlide || !firstSlide) return null;

    return targetSlide.offsetLeft - firstSlide.offsetLeft;
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    allFeatures.forEach((_, index) => {
      const slideLeft = getSlideLeftPosition(index);
      if (slideLeft === null) return;

      const distance = Math.abs(container.scrollLeft - slideLeft);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setActiveIndex(nearestIndex);
  }, [allFeatures, getSlideLeftPosition]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToSlide = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;

    const slideLeft = getSlideLeftPosition(index);
    if (slideLeft === null) return;

    container.scrollTo({ left: slideLeft, behavior: "smooth" });
  };

  if (isInApp) {
    const activeFeature = allFeatures[activeIndex];

    return (
      <section className={`${styles.allFeaturesCarousel} ${className}`}>
        <div className={styles.appSection}>
          <div className={styles.appHeader}>
            <Text level="h2" className={styles.appHeading}>
              Key features
            </Text>
          </div>

          <div className={styles.appCarouselWrapper}>
            <div
              ref={scrollRef}
              className={styles.appCarouselScroll}
            >
              {allFeatures.map((feature) => (
                <AllFeatureCard
                  key={feature.id}
                  feature={feature}
                  isDetailed={false}
                  isSellable={isSellable}
                  isMMV={isMMV}
                  isInApp
                />
              ))}
            </div>

            <div className={styles.appDots}>
              {allFeatures.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  className={`${styles.appDot} ${index === activeIndex ? styles.appDotActive : ""}`}
                  onClick={() => scrollToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {activeFeature && (
              <div className={styles.appFeatureInfo}>
                <Text className={styles.appFeatureTitle}>
                  {activeFeature.title}
                </Text>
                {/* {
                  activeFeature.description &&
                  <Text className={styles.appFeatureDescription}>
                    {activeFeature.description}
                  </Text>
                } */}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`${styles.allFeaturesCarousel} ${className}`}>
      <div className={`${styles.bottomSection}`}>
        <div className={styles.detailedCarousel}>
          <Carousel 
            heading={mmvName?`Key Features of ${mmvName}`:'Key Features'} 
            headingClassName={styles.carouselHeading} 
            arrowButtonClass={styles.arrowButton} 
            isDark={true} 
            className={styles.detailedCarouselContainer} 
            showMobileDots={true}
          >
            {allFeatures.map((feature) => (
              <AllFeatureCard
                key={feature.id}
                feature={feature}
                isDetailed={true}
                isSellable={isSellable}
                isMMV={isMMV}
              />
            ))}
          </Carousel>
        </div>
      </div>
    </section>
  );
});

AllFeaturesCarouselClient.displayName = "AllFeaturesCarouselClient";
